// src/components/modals/DeleteCategoryModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, AlertTriangle, Loader2 } from 'lucide-react'
import CategoryService from '../../services/categoryService'

const DeleteCategoryModal = ({ isOpen, onClose, onCategoryDeleted, category }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!category?._id) {
      console.error('❌ No category ID provided for deletion')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Enhanced debug logging
      console.log('🗑️ Deleting category:', {
        categoryId: category._id,
        categoryName: category.name,
        token: localStorage.getItem('tezlaa-access-token') ? 'Present' : 'Missing'
      })
      
      const response = await CategoryService.deleteCategory(category._id)
      
      if (response.success) {
        console.log('✅ Category deleted successfully:', response)
        
        // Notify parent component
        onCategoryDeleted(category._id)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Delete failed with response:', response)
        setError(response.message || 'Failed to delete category')
      }
    } catch (error) {
      console.error('❌ Error deleting category:', error)
      
      // Enhanced error handling
      let errorMessage = 'Failed to delete category. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Category not found. It may have already been deleted.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to delete this category. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message.includes('conflict') || error.message.includes('in use')) {
        errorMessage = 'Cannot delete category. It may be in use by products.'
      } else if (error.message.includes('products exist')) {
        errorMessage = 'Cannot delete category. Please move or delete associated products first.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setError(null)
    onClose()
  }

  if (!isOpen || !category) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-red-600">Delete Category</h2>
            <button 
              onClick={handleClose} 
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Warning Content */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <div>
                <h3 className="font-medium text-gray-900">Are you sure?</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            
            {/* Category Info */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm">
                <span className="font-medium">Category:</span> {category.name}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {category.description}
              </p>
              {category.products !== undefined && category.products > 0 && (
                <p className="text-sm text-orange-600 mt-2 font-medium">
                  ⚠️ This category has {category.products} associated products
                </p>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Category'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeleteCategoryModal